import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Outlet, useMatches } from "@tanstack/react-router";
import { deleteQueueGroupFn, queueGroupsQueryOptions } from "@/server/queues";
import { DashboardLayout } from "@/components/dashboard-layout";
import { Card, CardContent } from "@/components/ui/card";
import { useState } from "react";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { toast } from "sonner";

export const Route = createFileRoute("/_dashboard/queue-groups")({
  component: RouteComponent,
  loader: async () => {
    return {
      crumb: "Queue Groups",
    };
  },
});

function RouteComponent() {
  const queueGroupsQuery = useQuery(queueGroupsQueryOptions);
  const queueGroups = queueGroupsQuery.data ?? [];
  const matches = useMatches();
  const isRootQueueGroups = matches[matches.length - 1].fullPath === "/queue-groups";
  const queryClient = useQueryClient();
  const [selectedQueueGroup, setSelectedQueueGroup] = useState<{ id: number; name: string } | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  function handleDeleteClick(queueGroupId: number, queueGroupName: string) {
    setSelectedQueueGroup({ id: queueGroupId, name: queueGroupName });
    setDialogOpen(true);
  }

  const deleteQueueGroupMutation = useMutation({
    mutationFn: deleteQueueGroupFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["queueGroups"] });
      queueGroupsQuery.refetch();
      setDialogOpen(false);
      setSelectedQueueGroup(null);
      setLoading(false);
    },
  });

  async function handleConfirmDelete() {
    if (!selectedQueueGroup) return;
    setLoading(true);
    const deleteQueueGroupPromise = deleteQueueGroupMutation.mutateAsync({
      data: { queueGroupId: Number(selectedQueueGroup.id) },
    });
    toast.promise(deleteQueueGroupPromise, {
      loading: "Deleting queue group...",
      success: () => {
        return "Queue group deleted successfully";
      },
      error: "Failed to delete queue group",
    });
  }

  return (
    <>
      <DashboardLayout
        submenu={{
          items: queueGroups,
          onClick: (item: any, functionType: string, parent?: { name: string; id: string }) => {
            if (functionType === "delete") {
              handleDeleteClick(item.parent.id, item.parent.name);
            }
          },
          title: "Queue Groups",
          active: true,
          createNew: [{ label: "+ Create new queue group", href: "/queue-groups/new-group" }],
        }}
      >
        {isRootQueueGroups ? (
          <Card>
            <CardContent>
              <p className="text-md pb-20 pt-15 text-center">
                Please select a queue group from the submenu to view its details or manage it.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Outlet />
        )}
      </DashboardLayout>
      <ConfirmDialog
        open={dialogOpen}
        title="Delete queue group"
        description={
          selectedQueueGroup ? `Are you sure you want to delete the queue group "${selectedQueueGroup.name}"?` : ""
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setDialogOpen(false);
          setSelectedQueueGroup(null);
        }}
        loading={loading}
      />
    </>
  );
}
